import { ProjectsController } from 'controllers/Projects/ProjectsController'
import { ShowException } from 'store/Application/appActions'
import { CookieManager } from 'utils/cookieUtils'

export const imageTokenKeyName = 'imageToken'

interface ITokenStorage {
  getToken: (keyName: string) => string | null
  setToken: (keyName: string, token: string, expiration: string) => void
  eraseToken: (keyName: string) => void
}

const managersCache: Record<string, ImageTokenManager> = {}

class CookieStorage implements ITokenStorage {
  public getToken(keyName: string) {
    return CookieManager.getCookie(keyName)
  }

  public setToken(keyName: string, token: string, expiration: string) {
    CookieManager.setCookie(keyName, token, expiration)
  }
  public eraseToken(keyName: string) {
    CookieManager.eraseCookie(keyName)
  }
}

class MemoryStorage implements ITokenStorage {
  private storage: Record<
    string,
    {
      token: string
      expiration: string
    }
  > = {}

  public getToken(keyName: string) {
    const { token, expiration } = this.storage[keyName] || {}

    if (token && expiration && new Date(expiration) > new Date()) {
      return token
    }

    return null
  }

  public setToken(keyName: string, token: string, expiration: string) {
    this.storage[keyName] = {
      token,
      expiration,
    }
  }

  public eraseToken(keyName: string) {
    delete this.storage[keyName]
  }
}

export class ImageTokenManager {
  private static instances: Record<string, ImageTokenManager> = {}
  private storage: ITokenStorage
  private controller: ProjectsController

  private getImageTokenPromise: Promise<{
    token: string
    expires: string
  }>

  private constructor(private projectId: string) {
    // this.storage = new CookieStorage()
    this.storage = new MemoryStorage()
    this.controller = new ProjectsController()
  }

  public static getInstance(projectId?: string) {
    if (!this.instances[projectId || '_']) {
      this.instances[projectId || '_'] = new ImageTokenManager(projectId)
    }

    return this.instances[projectId || '_']
  }

  public subscribeToIsLoading(callback: (isLoading: boolean) => void) {
    return this.controller.subscribeToUpdates((status) => {
      callback(status.loading['get-image-token'])
    })
  }

  public async getImageToken() {
    try {
      const imageTokenFromStorage = this.storage.getToken(imageTokenKeyName)

      if (imageTokenFromStorage) {
        return imageTokenFromStorage
      }

      if (this.getImageTokenPromise) {
        return this.getImageTokenPromise
      }

      this.getImageTokenPromise = this.controller
        .GetProjectImageToken(this.projectId)
        .then((imageToken) => {
          if (imageToken) {
            this.setImageToken(
              imageTokenKeyName,
              imageToken.token,
              imageToken.expires,
            )
          }

          this.getImageTokenPromise = null

          return imageToken?.token
        })
        .catch((err) => {
          ShowException('image token', err)
          console.error(err)
          return null
        })

      return this.getImageTokenPromise
    } catch (err) {
      ShowException('image token', err)
      console.error(err)
      return null
    }
  }

  private setImageToken(keyName: string, token: string, expiration: string) {
    this.storage.setToken(keyName, token, expiration)
  }

  public eraseImageToken() {
    this.storage.eraseToken(imageTokenKeyName)
  }
}
